import { Close } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Modal, Tooltip } from "@mui/material";
import { styled as MuiStyled } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import DraggableList from "react-draggable-list";
import { styled } from "styled-components";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import Button from "../../../Button";
import ImageCropper from "../../../ImageCropper";
import ImageModal from "../../../ImageModal";
import { shouldAutoEnableCaptions } from "./captionAutoShow";
import Input from "../../../Input";
import TextView from "../../../TextView";
import MediaSelection from "../../Shared/MediaSelection";
import ListImageThumb from "./ListImageThumb";

const Container = styled.div`
  min-height: 40px;
  height: 6vh;
  align-items: center;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: auto auto auto 1fr auto;
  grid-gap: 8px;
`;

const DragIcon = MuiStyled(DragIndicatorIcon)`
  font-weight: 600;
  font-size: 25px;
  cursor: grab;
  color: #bbbbbb;
`;

// QA-54 (Kelly, 2026-07-31) discoverability: the caption used to be reachable
// only by guessing what an icon-only pencil did — its meaning appeared on hover
// and nowhere else. The row's own text is now the affordance: it states "Add a
// caption" when empty and opens the same editor on click or keyboard. 32px min
// height per the touch-target ruling; no new icon, no emoji, no motion.
const CaptionCell = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 0 4px;
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  color: ${(p) => (p.$empty ? colors.purple : "inherit")};
  font-style: ${(p) => (p.$empty ? "italic" : "normal")};

  &:hover {
    background: #f4f2fb;
  }

  &:focus-visible {
    outline: 2px solid ${colors.purple};
    outline-offset: 2px;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
  cursor: pointer;
  width: 100%;
  gap: 5px;
`;

const Icon = styled.img`
  height: 16px;
  width: 16px;
`;

// Every row action was a bare clickable <img>: no accessible name, no keyboard
// path, no focus ring — invisible to a screen reader and unreachable without a
// mouse. IconButton wraps the SAME glyph in a real, named, focusable button and
// keeps the 32px minimum touch target, so all five actions stay one another's
// equal instead of one becoming a button and four staying pictures.
const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 6px;
  &:focus-visible {
    outline: 2px solid ${colors.purple};
    outline-offset: 2px;
  }
`;

const ModalContainer = styled.div`
  position: absolute;
  width: 400px;
  background-color: white;
  box-shadow: 0px 0px 9.3px 0px #3b3b3b1c;
  padding: 16px;
  border-radius: 8px;
  top: 50%;
  right: 10%;
  overflow: hidden;
`;

const AddIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid #e1e1e1;
  background-color: ${colors.background};
  margin: auto;
  cursor: pointer;
  margin-top: 10px;
`;

const ModalHeader = styled.div`
  display: flex;
  background-color: ${colors.background};
  justify-content: space-between;
  padding: 16px;
`;

const ModalContent = styled.div`
  padding: 16px;
`;

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 100px 80px;
  grid-gap: 10px;
  justify-content: flex-end;
`;
export default function List({
  images = [],
  setImages,
  shallowImages = [],
  setShallowImages,
  onClickAdd,
  showCaptions = false,
  onAutoEnableCaptions,
  // QA-113 (Kelly, 2026-08-06): the Carousel settings pane already exposes an
  // "+ Add images" button and the always-visible media picker, so this centered
  // circle-plus was a redundant third add-trigger ("unnecessary"). Default TRUE
  // preserves the ImageGrid block, which relies on this button to reveal its
  // picker; the Carousel passes showAddButton={false}.
  showAddButton = true,
}) {
  const [openPreview, setOpenPreview] = useState(false);
  const [openMediaSelection, setOpenMediaSelection] = useState(false);
  const [openImageCropper, setOpenImageCropper] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const mediaRef = useRef(null);
  const containerRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mediaRef.current && !mediaRef.current.contains(event.target)) {
        setOpenMediaSelection(false);
        setCurrentItem(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mediaRef]);

  const _onListChange = (newList) => {
    setImages(newList);
  };

  const handleOpenMediaSelection = (item) => {
    setCurrentItem(item);
    setOpenMediaSelection(true);
  };

  const handleOpenImageCropper = (item) => {
    setCurrentItem(item);
    setOpenImageCropper(true);
  };

  const handleOpenPreview = (item) => {
    setCurrentItem(item);
    setOpenPreview(true);
  };

  const handleTitleChange = (id, value) => {
    const temp = [...images];
    const index = temp.findIndex((val) => val.id == id);
    temp[index] = { ...temp[index], title: value };
    setImages(temp);
    // QA-63 / Gordon 2026-08-03: typing a caption IS the intent to show it, so the
    // first non-empty caption on this block switches its captions on. The WHEN is
    // decided by shouldAutoEnableCaptions (pure, unit-tested) — notably never on a
    // clear, and never when the toggle is already on.
    if (shouldAutoEnableCaptions(value, showCaptions)) onAutoEnableCaptions?.();
  };

  const onDeleteItem = (id) => {
    setImages(images.filter((image) => image.id !== id));
    setShallowImages(shallowImages.filter((image) => image.id !== id));
  };

  const updateItemInArray = (array, itemId, newItemData) => {
    const index = array.findIndex((item) => item.id === itemId);
    if (index !== -1) {
      const updatedArray = [...array];
      updatedArray[index] = { ...updatedArray[index], ...newItemData };
      return updatedArray;
    }
    return array;
  };

  const onReplaceItem = (_, value) => {
    const newImage = typeof value === "string" ? { src: value } : value;

    const updatedImages = updateItemInArray(images, currentItem.id, newImage);
    setImages(updatedImages);

    const updatedShallowImages = updateItemInArray(
      shallowImages,
      currentItem.id,
      newImage
    );
    setShallowImages(updatedShallowImages);

    setOpenMediaSelection(false);
  };

  const handleEditImage = (image) => {
    const temp = [...images];
    const index = temp.findIndex((val) => val.id == currentItem.id);
    temp[index] = { ...temp[index], src: image };
    setImages(temp);
    setOpenMediaSelection(false);
  };

  const ListTemplate = ({ item, index, dragHandleProps }) => {
    const { onMouseDown, onTouchStart } = dragHandleProps;
    const [title, setTitle] = useState(item.title);
    const [openTitlePopup, setOpenTitlePopup] = useState(false);

    const handleChange = (e) => {
      const { value } = e.target;
      handleTitleChange(item.id, value);
    };

    return (
      <div>
        <Container>
          <span
            style={{
              fontFamily: "DM Sans",
              fontSize: "12px",
              fontWeight: 400,
              color: "#565556",
            }}
          >
            {" "}
            {index + 1}
          </span>
          <DragIcon
            className="disable-select dragHandle"
            onTouchStart={(e) => {
              e.preventDefault();
              e.target.style.backgroundColor = "blue";
              document.body.style.overflow = "hidden";
              onTouchStart(e);
            }}
            onMouseDown={(e) => {
              document.body.style.overflow = "hidden";
              onMouseDown(e);
            }}
            onTouchEnd={(e) => {
              e.target.style.backgroundColor = "black";
              document.body.style.overflow = "visible";
            }}
            onMouseUp={() => {
              document.body.style.overflow = "visible";
            }}
          />

          <ListImageThumb src={item.src} alt={item.title || ""} />

          <CaptionCell
            type="button"
            $empty={!item.title}
            onClick={() => setOpenTitlePopup(true)}
            aria-label={
              item.title
                ? `Edit caption: ${item.title}`
                : "Add a caption to this image"
            }
          >
            <TextView>{item.title || "Add a caption"}</TextView>
          </CaptionCell>
          <IconsContainer>
            <Tooltip title="Edit caption">
              <IconButton
                type="button"
                aria-label="Edit caption"
                onClick={() => setOpenTitlePopup(true)}
              >
                <Icon src={ASSETS.segmentEditor.EditPencil} alt="" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Image">
              <IconButton
                type="button"
                aria-label="Edit image"
                onClick={() => handleOpenImageCropper(item)}
              >
                <Icon src={ASSETS.EditImage} alt="" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Preview Image">
              <IconButton
                type="button"
                aria-label="Preview image"
                onClick={() => handleOpenPreview(item)}
              >
                <Icon src={ASSETS.segmentEditor.ViewV2} alt="" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Replace Image">
              <IconButton
                type="button"
                aria-label="Replace image"
                onClick={() => handleOpenMediaSelection(item)}
              >
                <Icon src={ASSETS.segmentEditor.ReplaceImage} alt="" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Image">
              <IconButton
                type="button"
                aria-label="Delete image"
                onClick={() => onDeleteItem(item.id)}
              >
                <Icon src={ASSETS.segmentEditor.DeleteOutline} alt="" />
              </IconButton>
            </Tooltip>
          </IconsContainer>
        </Container>
        {openTitlePopup && (
          <Modal open={openTitlePopup} onClose={() => setOpenTitlePopup(false)}>
            <ModalContainer style={{ padding: 0 }}>
              <ModalHeader>
                <TextView variant="subtitle">Edit caption</TextView>
                <Close
                  style={{ cursor: "pointer" }}
                  onClick={() => setOpenTitlePopup(false)}
                />
              </ModalHeader>
              <ModalContent>
                <Input
                  variant={"secondary"}
                  aria-label="Caption"
                  placeholder="Add a caption"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <ButtonsContainer>
                  <Button
                    variant={"secondary"}
                    onClick={() => setOpenTitlePopup(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={() => handleTitleChange(item.id, title)}>
                    Save
                  </Button>
                </ButtonsContainer>
              </ModalContent>
            </ModalContainer>
          </Modal>
        )}
      </div>
    );
  };

  return (
    <div>
      <DraggableList
        itemKey="id"
        // template={ListTemplate}
        template={(props) => (
          <ListTemplate
            {...props}
            index={images.findIndex((i) => i.id === props.item.id)}
          />
        )}
        list={images}
        onMoveEnd={(newList) => _onListChange(newList)}
        container={() => containerRef.current}
      />

      {showAddButton && (
        <AddIconContainer onClick={onClickAdd}>
          <AddIcon style={{ color: colors.purple }} />
        </AddIconContainer>
      )}

      {currentItem && (
        <>
          {/* QA-63 (Kelly, 2026-08-03): "previewing image. The caption is also not
              there. And that was supposed to be fixed."  QA-54 / PR #2498 fixed the
              identical hardcoded `showTitle={false}` literal in Shared/ImageBlock,
              but this sibling sink — the row's own "Preview Image" eye, shared by
              the Carousel AND Image Grid blocks — was missed by that sweep, so the
              caption a user had just typed with the pencil vanished on preview.
              The rule is now derived from the data, exactly like ImageBlock's, so
              the two enlargement paths cannot silently diverge again. */}
          <ImageModal
            showTitle={Boolean(currentItem?.title)}
            open={openPreview}
            images={[currentItem]}
            handleClose={() => setOpenPreview(false)}
          />

          <Modal
            open={openMediaSelection}
            onClose={() => setOpenMediaSelection(false)}
          >
            <ModalContainer>
              <MediaSelection
                type="image"
                isGifAllowed
                handleChange={onReplaceItem}
                media={currentItem}
              />
            </ModalContainer>
          </Modal>
          <ImageCropper
            open={openImageCropper}
            handleClose={() => setOpenImageCropper(false)}
            src={currentItem?.src}
            handleEditImage={handleEditImage}
          />
        </>
      )}
    </div>
  );
}
